import {
  type DocumentTerms,
  TERMS_KEYS,
  TERMS_LABELS,
  TERMS_MAX_LENGTH,
  type TermsKey,
  type UpdateDocumentTermsInput,
} from '@facture/contracts'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { SaveIcon, ScrollTextIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Textarea } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/misc'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/lib/api'
import { companyQuery } from '@/lib/queries'
import { useCan } from '@/lib/session'

export const Route = createFileRoute('/_app/settings/terms')({
  staticData: { crumb: 'Términos y condiciones' },
  loader: ({ context }) => context.queryClient.ensureQueryData(companyQuery()),
  pendingComponent: () => <Skeleton className="h-80 rounded-xl" />,
  component: TermsSettings,
})

type Drafts = Record<TermsKey, string>

const toDrafts = (terms: DocumentTerms): Drafts =>
  Object.fromEntries(TERMS_KEYS.map((k) => [k, terms[k] ?? ''])) as Drafts

function TermsSettings() {
  const { data: company } = useSuspenseQuery(companyQuery())
  const canManage = useCan('settings:manage')
  const queryClient = useQueryClient()
  const saved = toDrafts(company.documentTerms ?? {})
  const [drafts, setDrafts] = useState<Drafts>(saved)
  const [tab, setTab] = useState<TermsKey>('FE')

  const dirtyKeys = TERMS_KEYS.filter((k) => drafts[k].trim() !== saved[k].trim())
  const save = useMutation({
    mutationFn: (input: UpdateDocumentTermsInput) => api.put<DocumentTerms>('/company/terms', input),
    onSuccess: (terms) => {
      setDrafts(toDrafts(terms))
      queryClient.invalidateQueries({ queryKey: companyQuery().queryKey })
      toast.success('Términos guardados')
    },
  })

  const submit = () => {
    const input: UpdateDocumentTermsInput = {}
    for (const k of dirtyKeys) input[k] = drafts[k].trim() || null
    save.mutate(input)
  }

  const fallback =
    tab === 'FEC'
      ? 'Si lo dejás vacío se usa el de Factura y, si tampoco hay, el texto general de Datos generales.'
      : 'Si lo dejás vacío se usa el texto general de Datos generales.'

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <ScrollTextIcon className="size-4" /> Términos y condiciones
          </CardTitle>
          <CardDescription className="mt-1.5">
            Un texto por tipo de comprobante. Se cargan solos al crear uno nuevo y salen en el PDF; en cada
            comprobante podés cambiarlos sin afectar los de Configuración.{' '}
            <Link to="/settings/company" className="underline underline-offset-2">
              Texto general de respaldo
            </Link>
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Tabs value={tab} onValueChange={(v) => setTab(v as TermsKey)} className="max-w-full">
          <TabsList aria-label="Tipo de comprobante" className="w-full justify-start md:w-auto">
            {TERMS_KEYS.map((k) => (
              <TabsTrigger key={k} value={k}>
                {TERMS_LABELS[k]}
                {dirtyKeys.includes(k) ? (
                  <span
                    role="img"
                    aria-label="con cambios sin guardar"
                    className="size-1.5 rounded-full bg-primary"
                  />
                ) : drafts[k].trim() ? (
                  <span
                    role="img"
                    aria-label="con texto"
                    className="size-1.5 rounded-full bg-muted-foreground/50"
                  />
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Field
          label={`Términos de ${TERMS_LABELS[tab].toLowerCase()}`}
          hint={
            <>
              {fallback}{' '}
              <span className="tabular-nums">
                {drafts[tab].length}/{TERMS_MAX_LENGTH}
              </span>
            </>
          }
        >
          {(c) => (
            <Textarea
              {...c}
              value={drafts[tab]}
              onChange={(e) => setDrafts((d) => ({ ...d, [tab]: e.target.value.slice(0, TERMS_MAX_LENGTH) }))}
              rows={8}
              disabled={!canManage}
              placeholder="Ej.: Pago a 30 días. Transferencias a la cuenta IBAN CR…"
            />
          )}
        </Field>
      </CardContent>
      {canManage && (
        <CardFooter className="justify-between">
          <span className="text-muted-foreground text-xs">
            {dirtyKeys.length ? 'Tenés cambios sin guardar' : 'Todo guardado'}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={!dirtyKeys.length} onClick={() => setDrafts(saved)}>
              Descartar
            </Button>
            <Button disabled={!dirtyKeys.length || save.isPending} onClick={submit}>
              <SaveIcon /> Guardar
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  )
}
